import React, { useCallback, useState } from 'react'
import { debounce } from 'lodash'
import SignUpService from '@/app-domains/user/onboarding/signup.service'
import { InputWithSelect } from '@/components/InputWithSelect/InputWithSelect'
import { Button } from '@/components/Button/Button'
import { SimpleBanner } from '@/components/Banners/SimpleBanner'
import { ReactComponent as SuccessIcon } from '@/assets/icons/success-icon.svg'
import { Link } from 'react-router-dom'
import AuthService from '@/app-domains/user/auth/Auth.service'
import ToastService from '@/components/Toast/Toast.service'

import './ChooseSubdomainStep.scoped.sass'
import sessionService from '@/app-domains/user/session/session.service'
import commonsService from '@/services/commons.service'

export function ChooseSubdomainStep () {
  const [domain, setDomain] = useState('')
  // eslint-disable-next-line
  const [domainOptions, setDomainOptions] = useState(
    [
      {
        type: 'group',
        name: 'Available domains',
        items: [
          { label: '.boxes.im', value: '.boxes.im' },
          { label: '.boxesemail.com', value: '.boxesemail.com' },
        ]
      }
    ]
  )
  // eslint-disable-next-line
  const [selectedDomain, setSelectedDomain] = useState({ label: '.boxes.im', value: '.boxes.im' })
  const [submitting, setSubmitting] = useState(false)
  const [isDomainAvailable, setIsDomainAvailable] = useState(false)
  const debouncedAvailabilityCheck = useCallback(debounce(checkSubdomainAvailability, 1000), [])

  async function checkSubdomainAvailability (domainName) {
    if (domainName.length === 0) return
    
    const domainBeforeTLD = domainName.split('.')[0]
    // Safari doesn't support lookbehind operator neither lookahead, that's why we check the first and last chars manually
    if (!(
      /^([A-Za-z0-9-]{1,63})$/.test(domainBeforeTLD) &&
      domainBeforeTLD[0] !== '-' &&
      domainBeforeTLD[domainBeforeTLD.length - 1] !== '-'
    )) {
      return ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'Domain has an invalid format',
      })
    }
    setSubmitting(true)
    ToastService.show({
      type: 'info',
      title: 'Availability',
      message: 'Checking subdomain availability ... ⏲',
    })
    const { available } = await SignUpService.checkIfSubdomainIsAvailable(domainName)
    setIsDomainAvailable(available)
    setSubmitting(false)
    if (available) {
      return ToastService.show({
        type: 'success',
        title: 'Congratulations!',
        message: `Your subdomain '${domainName}' is available!`,
      })
    }
    return ToastService.show({
      type: 'error',
      title: 'We are sorry',
      message: 'That subdomain is not available at the moment',
    })
  }

  const handleDomainChange = (value) => {
    if (!(/^[A-Za-z0-9-]{0,63}$/.test(value))) {
      return
    }

    setDomain(value)

    if (value.length === 0) {
      debouncedAvailabilityCheck('')
      return
    }

    setIsDomainAvailable(false)

    const completeDomain = value + selectedDomain.value
    debouncedAvailabilityCheck(completeDomain)
  }

  const handleTLDChange = (value) => {
    const selectedDomain = domainOptions[0].items.find((item) => item.value === value)
    setSelectedDomain(selectedDomain)

    handleDomainChange(domain)
  }

  const checkDomainValue = () => {
    if (!domain) {
      ToastService.show({
        type: 'error',
        title: 'Error',
        message: 'Subdomain is empty'
      })
      setSubmitting(false)
    }
    return !!domain
  }

  const onSubmitHandler = (e) => {
    if (submitting) return e.preventDefault()
    if (!checkDomainValue()) return

    setSubmitting(true)
    const completeDomain = domain + selectedDomain.value
    SignUpService.checkIfSubdomainIsAvailable(completeDomain)
      .then(async () => {
        const response = await SignUpService.startFreeTrial(completeDomain)
        if (response) {
          window.location.href = '/?welcome=true'
        }
      })
      .catch(() => {
        setIsDomainAvailable(false)
        setSubmitting(false)
      })
      .finally(setSubmitting(false))
    e?.preventDefault()
  }

  return (
    <div className='domain-selection-parent'>
      <Link className="logout-link" onClick={AuthService.signOut}>Log out</Link>
      <div className='domain-selection'>
        <div className="domain-selection-title">Amazing, let's choose your subdomain!</div>
        <InputWithSelect onInput={handleDomainChange} label="Select your perfect subdomain" onSelectChange={handleTLDChange} options={domainOptions} />
      
        { isDomainAvailable && <div className='select-subdomain-summary'>
          <SimpleBanner type='success'>
            <p>
              <SuccessIcon style={{ display: 'inline-block', position: 'relative', top: '-1px', marginRight: '8px' }}/>
              Congratulations! <br/>
              Your free trial period using <strong>{domain + selectedDomain.value}</strong> subdomain is ready to be started!
            </p>
          </SimpleBanner>
        </div>}
        { sessionService.getMe().me.status === 'onboarding' && <div className="free-trial-request">
          <a onClick={() => commonsService.goToPage('/auth/signup/domain-selection')}>I want to have my own domain</a>
          </div> }
        <div className="submit-subdomain">
          <Button
            size='large'
            content="Join Boxes"
            click={onSubmitHandler}
            disabled={submitting || !isDomainAvailable } />
        </div>
      </div>
    </div>
  )
}
